import { faTag } from '@fortawesome/pro-regular-svg-icons'
import { faBolt, faBox, faHandshake } from '@fortawesome/pro-solid-svg-icons'
import { styled } from 'styled-components'

import { useFlag } from '@b-stock/fe-core/bstock-next/helpers'
import {
  type OrchestratedAuctionListingDetails,
  type OrchestratedOfferingListingDetails,
} from '@b-stock/fe-core/bstock-next/queries'
import { Tag, TagType, FormattedMessage } from '@b-stock/fe-core/bstock-react'
import { ListingEnums } from '@b-stock/fe-core/locale'

import { useSellerCollectsListingTagAnalytics } from '@/buyer-portal/helpers/sellerCollectsAnalytics'
import { getListingSaleKind } from '@/buyer-portal/helpers/sellerCollectsSaleKind'
import {
  CreditCardTag,
  CreditCardNotAcceptedTag,
} from '@/components/listings/CreditCardTag'
import { SellerCollectsTag } from '@/components/listings/SellerCollectsTag'
import { TagIcon, TagWithIcon } from '@/components/listings/shared/TagWithIcon'
import { FeatureFlags } from '@/helpers/featureFlags'

import { useListingTagsModel } from './useListingTagsModel'

const ns = 'Listing.tags'

const TagContainer = styled.div`
  align-items: center;
  display: flex;
  flex: 1;
  flex-wrap: wrap;
  gap: 0.25rem;
  min-width: 0;
`

/**
 * The redesigned listing tags: icon-prefixed type / pricing / buy-now tags
 * plus inventory-type and condition tags.
 */
export const ListingTagsV2 = ({
  listing,
}: {
  listing:
    | OrchestratedOfferingListingDetails
    | OrchestratedAuctionListingDetails
}) => {
  const {
    listingType,
    pricingStrategy,
    inventoryTypes,
    condition,
    isBuyNow,
    showListingTypeTag,
    showPricingStrategyTag,
    isSellerCollects,
    sellerName,
    hasBuyerPremium,
    sellerArrangesShipping,
    isBuyerPremiumSettled,
  } = useListingTagsModel(listing)
  const ccSurfacingEnabled = useFlag(FeatureFlags.ENABLE_CC_SURFACING)
  const sellerCollectsEnabled = useFlag(
    FeatureFlags.SELLER_COLLECTS_BUYER_EXPERIENCE
  )
  const showSellerCollectsTag = sellerCollectsEnabled && isSellerCollects
  // The listing view is reported from here rather than the page: this row is
  // rendered once per listing page and already holds the seller's fee model.
  const { onExplainerOpened } = useSellerCollectsListingTagAnalytics(listing, {
    isSellerCollects,
    hasBuyerPremium,
    isBuyerPremiumSettled,
  })
  const showCreditCardTag =
    ccSurfacingEnabled && listing.isCreditCardEnabled === true
  const showCreditCardNotAcceptedTag =
    ccSurfacingEnabled && listing.isCreditCardEnabled === false

  return (
    <TagContainer>
      {showListingTypeTag ? (
        <Tag
          type={listingType === 'FULFILLMENT' ? TagType.info : TagType.normal}
        >
          {listingType === 'SPOT' ? (
            <TagWithIcon>
              <svg
                width="15"
                height="12"
                viewBox="0 0 15 12"
                fill="none"
                aria-hidden="true"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M11.5312 3.14062L10.7344 0.773438C10.5938 0.328125 10.1719 0 9.67969 0H2.29688C1.80469 0 1.38281 0.328125 1.24219 0.773438L0.046875 4.33594C0 4.45312 0 4.57031 0 4.6875V10.875C0 11.5078 0.492188 12 1.125 12H10.5C12.9844 12 15 9.98438 15 7.5C15 5.39062 13.5234 3.60938 11.5312 3.14062ZM6.5625 1.125H9.67969L10.3125 3.02344C9.14062 3.07031 8.08594 3.5625 7.3125 4.3125H6.5625V1.125ZM2.29688 1.125H5.4375V4.3125H1.24219L2.29688 1.125ZM1.125 10.875V5.4375H6.49219C6.16406 6.07031 6 6.77344 6 7.5C6 8.85938 6.58594 10.0547 7.52344 10.875H1.125ZM10.5 10.875C8.625 10.875 7.125 9.375 7.125 7.5C7.125 5.64844 8.625 4.125 10.5 4.125C12.3516 4.125 13.875 5.64844 13.875 7.5C13.875 9.375 12.3516 10.875 10.5 10.875ZM12 6.07031C11.9297 6 11.8125 6 11.7422 6.07031L10.1016 7.6875L9.39844 6.96094C9.32812 6.89062 9.21094 6.89062 9.14062 6.96094L8.69531 7.40625C8.625 7.47656 8.625 7.59375 8.69531 7.66406L9.96094 8.95312C10.0312 9.02344 10.1484 9.02344 10.2188 8.95312L12.4453 6.77344C12.5156 6.70312 12.5156 6.58594 12.4453 6.49219L12 6.07031Z"
                  fill="currentColor"
                />
              </svg>
              <FormattedMessage id={`${ns}.listingType.${listingType}`} />
            </TagWithIcon>
          ) : listingType === 'CONTRACT' ? (
            <TagWithIcon>
              <TagIcon icon={faHandshake} aria-hidden="true" />
              <FormattedMessage id={`${ns}.listingType.${listingType}`} />
            </TagWithIcon>
          ) : listingType === 'FULFILLMENT' ? (
            <TagWithIcon>
              <TagIcon icon={faBox} aria-hidden="true" />
              <FormattedMessage id={`${ns}.listingType.${listingType}`} />
            </TagWithIcon>
          ) : (
            <FormattedMessage id={`${ns}.listingType.${listingType}`} />
          )}
        </Tag>
      ) : null}
      {showPricingStrategyTag ? (
        <Tag type={TagType.info}>
          {pricingStrategy === 'MAKE_AN_OFFER' ? (
            <TagWithIcon>
              <TagIcon icon={faTag} aria-hidden="true" />
              <FormattedMessage
                id={`${ns}.pricingStrategy.${pricingStrategy}`}
              />
            </TagWithIcon>
          ) : (
            <FormattedMessage id={`${ns}.pricingStrategy.${pricingStrategy}`} />
          )}
        </Tag>
      ) : null}
      {pricingStrategy === 'AUCTION' && isBuyNow ? (
        <Tag type={TagType.info}>
          <TagWithIcon>
            <TagIcon icon={faBolt} aria-hidden="true" />
            <FormattedMessage id={`${ns}.buyNow`} />
          </TagWithIcon>
        </Tag>
      ) : null}
      {inventoryTypes.map((type) => (
        <Tag key={type} type={TagType.info}>
          <ListingEnums.InventoryType.Component value={type} />
        </Tag>
      ))}
      {condition ? <Tag type={TagType.info}>{condition}</Tag> : null}
      {showSellerCollectsTag && (
        <SellerCollectsTag
          sellerName={sellerName}
          hasBuyerPremium={hasBuyerPremium}
          sellerArrangesShipping={sellerArrangesShipping}
          onExplainerOpened={onExplainerOpened}
          saleKind={getListingSaleKind(listing)}
        />
      )}
      {showCreditCardTag && <CreditCardTag />}
      {showCreditCardNotAcceptedTag && <CreditCardNotAcceptedTag />}
    </TagContainer>
  )
}
